import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../mock';
import ChartCard from '../components/ChartCard';
import PeriodFilter from '../components/PeriodFilter';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { subDays, isAfter, isBefore, parseISO, format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Sun, Zap, TrendingUp, Battery, Settings, RefreshCw, Wifi, WifiOff, Gauge, Clock, Activity, Play, Pause, Globe, Leaf, TreeDeciduous, CloudSun, Search, Filter, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import ExportMenu from '../components/ExportMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cores para dispositivos
const DEVICE_COLORS = ['#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

// Função para formatar números com vírgula
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(decimals).replace('.', ',');
};

const Fotovoltaico = () => {
  const navigate = useNavigate();
  const [periodFilter, setPeriodFilter] = useState({ type: 'preset', days: 30 });
  
  // Estados para dados reais
  const [apiConnected, setApiConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [readings, setReadings] = useState([]);
  
  // Auto-refresh settings
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60); // segundos
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  // ===== Huawei FusionSolar =====
  // A API Huawei publica novos pontos a cada 5 min, fixamos esse intervalo no frontend
  const HUAWEI_REFRESH_MS = 5 * 60 * 1000;
  const [huaweiStations, setHuaweiStations] = useState([]);
  const [huaweiSummary, setHuaweiSummary] = useState(null);
  const [huaweiRealtime, setHuaweiRealtime] = useState({}); // { station_id: data }
  const [refreshingHuawei, setRefreshingHuawei] = useState(false);
  const [lastHuaweiRefresh, setLastHuaweiRefresh] = useState(null);
  const huaweiIntervalRef = useRef(null);

  // Histórico diário compilado (Huawei) para tabela filtrável + exportação
  const [huaweiDailyHistory, setHuaweiDailyHistory] = useState([]); // [{date, station_id, local, daily_kwh, real_kw, ...}]
  const [visibleStationIds, setVisibleStationIds] = useState(new Set());
  const [tableSearch, setTableSearch] = useState('');
  const [tableFilterStation, setTableFilterStation] = useState('all');

  const refreshHuaweiData = useCallback(async (showToast = false) => {
    setRefreshingHuawei(true);
    try {
      const stationsRes = await fetch(`${API_URL}/api/huawei/stations`);
      const stationsData = await stationsRes.json();
      const stations = Array.isArray(stationsData) ? stationsData : [];
      setHuaweiStations(stations);

      // Fetch realtime for each station (saves snapshot)
      const realtimeMap = {};
      for (const s of stations) {
        try {
          const r = await fetch(`${API_URL}/api/huawei/stations/${s.id}/realtime?save=true`);
          const d = await r.json();
          if (d.success) realtimeMap[s.id] = d;
        } catch (e) { /* skip */ }
      }
      setHuaweiRealtime(realtimeMap);

      const summaryRes = await fetch(`${API_URL}/api/huawei/summary`);
      const summaryData = await summaryRes.json();
      setHuaweiSummary(summaryData);

      setLastHuaweiRefresh(new Date());
      if (showToast && stations.length > 0) {
        toast.success(`Estações Huawei atualizadas (${stations.length})`);
      }
    } catch (error) {
      console.error('Erro ao atualizar Huawei:', error);
    }
    setRefreshingHuawei(false);
  }, []);

  // Histórico diário de cada estação Huawei (para tabela + exportação)
  const loadHuaweiDailyHistory = useCallback(async (stations, dias) => {
    if (!stations || stations.length === 0) {
      setHuaweiDailyHistory([]);
      return;
    }
    try {
      const allRows = [];
      for (const s of stations) {
        try {
          const res = await fetch(`${API_URL}/api/huawei/stations/${s.id}/history?dias=${dias}`);
          if (!res.ok) continue;
          const data = await res.json();
          (data.daily || []).forEach((d) => {
            allRows.push({
              date: d.date,
              station_id: s.id,
              local: s.local,
              descricao: s.descricao,
              installed_power_kw: s.installed_power_kw || 0,
              daily_kwh: d.daily_energy_kwh || 0,
              cumulative_kwh: d.cumulative_energy_kwh || 0,
              real_time_power_kw: d.real_time_power_kw || 0,
              timestamp: d.timestamp,
            });
          });
        } catch (e) { /* ignore station */ }
      }
      setHuaweiDailyHistory(allRows.sort((a, b) => b.date.localeCompare(a.date)));
    } catch (e) {
      console.error('Erro ao carregar histórico Huawei:', e);
    }
  }, []);

  // Obter leituras em tempo real
  const refreshReadings = useCallback(async (showToast = false) => {
    if (refreshing) return;
    setRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/shelly/refresh-all`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        setLastRefresh(new Date());
        if (showToast) {
          toast.success(`Leituras atualizadas: ${data.refreshed}/${data.total}`);
        }
        
        // Atualizar summary
        const summaryResponse = await fetch(`${API_URL}/api/fotovoltaico/summary`);
        const summaryData = await summaryResponse.json();
        if (summaryData.success) {
          setSummary(summaryData);
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar leituras:', error);
    }
    
    setRefreshing(false);
  }, [refreshing]);

  // Carregar histórico de leituras (15 em 15 minutos)
  const loadReadingsHistory = useCallback(async () => {
    try {
      // Carregar leituras de todos os dispositivos
      const devicesResponse = await fetch(`${API_URL}/api/shelly/devices`);
      const devicesData = await devicesResponse.json();
      
      if (devicesData && devicesData.length > 0) {
        const allReadings = [];
        
        for (const device of devicesData) {
          const response = await fetch(`${API_URL}/api/shelly/readings/${device.device_id}?dias=1`);
          const data = await response.json();
          
          if (data.readings) {
            data.readings.forEach(reading => {
              allReadings.push({
                ...reading,
                descricao: device.descricao || device.device_id,
                local: device.local
              });
            });
          }
        }
        
        // Ordenar por timestamp
        allReadings.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setReadings(allReadings);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  }, []);

  // Carregar dados iniciais
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const devicesResponse = await fetch(`${API_URL}/api/shelly/devices`);
      const devicesData = await devicesResponse.json();
      setDevices(devicesData || []);

      if (devicesData && devicesData.length > 0) {
        setApiConnected(true);

        // Carregar resumo
        const summaryResponse = await fetch(`${API_URL}/api/fotovoltaico/summary`);
        const summaryData = await summaryResponse.json();
        if (summaryData.success) {
          setSummary(summaryData);
        }

        // Carregar previsão
        const forecastResponse = await fetch(`${API_URL}/api/fotovoltaico/forecast`);
        const forecastData = await forecastResponse.json();
        if (forecastData.success) {
          setForecast(forecastData.forecast || []);
        }

        // Carregar dados diários
        const dias = periodFilter.days || 30;
        const dailyResponse = await fetch(`${API_URL}/api/fotovoltaico/daily?dias=${dias}`);
        const dailyDataResult = await dailyResponse.json();
        setDailyData(dailyDataResult.daily || []);
        
        // Carregar histórico de leituras
        await loadReadingsHistory();
        
        // Fazer primeira leitura
        await refreshReadings(false);
      } else {
        setApiConnected(false);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setApiConnected(false);
    }
    setLoading(false);
  }, [periodFilter.days, loadReadingsHistory, refreshReadings]);

  // Carregar dados ao montar
  useEffect(() => {
    loadData();
    refreshHuaweiData(false);
  }, [loadData, refreshHuaweiData]);

  // Auto-refresh dedicado para Huawei (intervalo fixo: 5 min, conforme API)
  useEffect(() => {
    huaweiIntervalRef.current = setInterval(() => {
      refreshHuaweiData(false);
    }, HUAWEI_REFRESH_MS);
    return () => {
      if (huaweiIntervalRef.current) clearInterval(huaweiIntervalRef.current);
    };
  }, [refreshHuaweiData, HUAWEI_REFRESH_MS]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && apiConnected) {
      // Iniciar countdown
      setCountdown(refreshInterval);
      
      // Interval para countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            return refreshInterval;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Interval para refresh
      intervalRef.current = setInterval(() => {
        refreshReadings(false);
        loadReadingsHistory();
      }, refreshInterval * 1000);
      
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setCountdown(0);
    }
  }, [autoRefresh, refreshInterval, apiConnected, refreshReadings, loadReadingsHistory]);

  // Carregar histórico diário Huawei sempre que estações ou período mudam
  useEffect(() => {
    if (huaweiStations.length === 0) return;
    setVisibleStationIds((prev) => {
      if (prev.size === 0) return new Set(huaweiStations.map((s) => s.id));
      return prev;
    });
    const dias = periodFilter.days || 30;
    loadHuaweiDailyHistory(huaweiStations, dias);
  }, [huaweiStations, periodFilter.days, loadHuaweiDailyHistory]);

  // Dados simulados filtrados
  const filteredMockData = useMemo(() => {
    if (periodFilter.type === 'preset') {
      const cutoffDate = subDays(new Date(), periodFilter.days);
      return mockData.fotovoltaico.filter(item => {
        const itemDate = parseISO(item.data);
        return isAfter(itemDate, cutoffDate);
      });
    } else {
      return mockData.fotovoltaico.filter(item => {
        const itemDate = parseISO(item.data);
        return isAfter(itemDate, periodFilter.startDate) && isBefore(itemDate, periodFilter.endDate);
      });
    }
  }, [periodFilter]);

  const mockChartData = useMemo(() => {
    const grouped = {};
    filteredMockData.forEach(item => {
      if (!grouped[item.data]) {
        grouped[item.data] = { data: item.data, producao: 0, excedente: 0 };
      }
      grouped[item.data].producao += item.producao;
      grouped[item.data].excedente += item.excedente;
    });
    return Object.values(grouped);
  }, [filteredMockData]);

  // Dados para gráficos
  const chartData = apiConnected && dailyData.length > 0 ? dailyData.map(d => ({
    data: d.date,
    producao: d.energy_kwh,
    excedente: d.energy_kwh * 0.3
  })) : mockChartData;

  // Agrupar leituras por intervalos de 15 minutos
  const readings15min = useMemo(() => {
    if (readings.length === 0) return [];
    
    const grouped = {};
    readings.forEach(reading => {
      const date = new Date(reading.timestamp);
      // Arredondar para 15 minutos
      const minutes = Math.floor(date.getMinutes() / 15) * 15;
      date.setMinutes(minutes, 0, 0);
      const key = date.toISOString();
      
      if (!grouped[key]) {
        grouped[key] = {
          timestamp: key,
          readings: [],
          total_power: 0,
          total_energy: 0,
          devices: new Set()
        };
      }
      
      grouped[key].readings.push(reading);
      grouped[key].total_power += reading.power_w || 0;
      grouped[key].total_energy = Math.max(grouped[key].total_energy, reading.energy_kwh || 0);
      grouped[key].devices.add(reading.descricao || reading.device_id);
    });
    
    return Object.values(grouped)
      .map(g => ({
        ...g,
        avg_power: g.readings.length > 0 ? g.total_power / g.readings.length : 0,
        devices_count: g.devices.size,
        devices_list: Array.from(g.devices).join(', ')
      }))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 50); // Últimos 50 registos
  }, [readings]);

  // Totais
  const totals = useMemo(() => {
    let producao = 0;
    let potenciaW = 0;
    let instalada = 0;
    let online = 0;
    let totalDevs = 0;

    if (apiConnected && summary) {
      producao += summary.total_energy_kwh || 0;
      potenciaW += summary.total_power_w || 0;
      instalada += summary.total_installed_kw || 0;
      online += summary.devices_online || 0;
      totalDevs += summary.devices_count || 0;
    } else if (!apiConnected) {
      producao += filteredMockData.reduce((sum, item) => sum + item.producao, 0);
    }

    if (huaweiSummary && huaweiSummary.success) {
      potenciaW += (huaweiSummary.total_real_time_power_kw || 0) * 1000;
      producao += huaweiSummary.total_cumulative_energy_kwh || 0;
      instalada += huaweiSummary.total_installed_kw || 0;
      online += huaweiSummary.stations_online || 0;
      totalDevs += huaweiSummary.stations_count || 0;
    }

    return {
      producaoTotal: producao,
      potenciaAtual: potenciaW,
      potenciaInstalada: instalada,
      dispositivosOnline: online,
      dispositivosTotal: totalDevs
    };
  }, [apiConnected, summary, filteredMockData, huaweiSummary]);

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatNumber(entry.value, 2)} {entry.name.includes('kW') ? 'kW' : 'kWh'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sun className="h-6 w-6 text-orange-500" />
            Fotovoltaico
          </h1>
          <p className="text-gray-500">Monitorização de produção solar em tempo real</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className={apiConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
            {apiConnected ? (
              <><Wifi className="h-3 w-3 mr-1" /> {devices.length} Dispositivos Shelly</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> Dados Simulados</>
            )}
          </Badge>
          {huaweiStations.length > 0 && (
            <Badge className="bg-orange-100 text-orange-800">
              <Globe className="h-3 w-3 mr-1" /> {huaweiStations.length} Estação{huaweiStations.length > 1 ? 'es' : ''} Huawei
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate('/configuracoes/fotovoltaico')}>
            <Settings className="h-4 w-4 mr-2" />
            Configurar
          </Button>
          <PeriodFilter value={periodFilter.days?.toString()} onChange={setPeriodFilter} />
        </div>
      </div>

      {/* Auto-Refresh Controls */}
      {apiConnected && (
        <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="auto-refresh"
                    checked={autoRefresh}
                    onCheckedChange={setAutoRefresh}
                  />
                  <Label htmlFor="auto-refresh" className="flex items-center gap-2 cursor-pointer">
                    {autoRefresh ? (
                      <Play className="h-4 w-4 text-green-600" />
                    ) : (
                      <Pause className="h-4 w-4 text-gray-400" />
                    )}
                    Atualização Automática
                  </Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Intervalo:</Label>
                  <Select 
                    value={refreshInterval.toString()} 
                    onValueChange={(v) => setRefreshInterval(parseInt(v))}
                  >
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 segundos</SelectItem>
                      <SelectItem value="60">1 minuto</SelectItem>
                      <SelectItem value="120">2 minutos</SelectItem>
                      <SelectItem value="300">5 minutos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {autoRefresh && countdown > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    Próxima atualização: <span className="font-mono font-semibold">{countdown}s</span>
                  </div>
                )}
                
                {lastRefresh && (
                  <div className="text-sm text-gray-500">
                    Última: {format(lastRefresh, 'HH:mm:ss', { locale: pt })}
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { refreshReadings(true); loadReadingsHistory(); }}
                  disabled={refreshing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Atualizar Agora
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Potência Atual</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatNumber(totals.potenciaAtual, 0)} W
                </p>
                {apiConnected && <Badge className="mt-1 bg-green-100 text-green-800 text-xs">Tempo Real</Badge>}
              </div>
              <Zap className="h-8 w-8 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Energia Total</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatNumber(totals.producaoTotal, 1)} kWh
                </p>
                <p className="text-xs text-gray-400">
                  Instalado: {formatNumber(totals.potenciaInstalada, 1)} kW
                </p>
              </div>
              <Sun className="h-8 w-8 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Eficiência</p>
                <p className="text-2xl font-bold text-green-600">
                  {totals.potenciaInstalada > 0 
                    ? formatNumber((totals.potenciaAtual / (totals.potenciaInstalada * 1000)) * 100, 1)
                    : '0'
                  }%
                </p>
                <p className="text-xs text-gray-400">da capacidade instalada</p>
              </div>
              <Gauge className="h-8 w-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Dispositivos</p>
                <p className="text-2xl font-bold text-blue-600">
                  {totals.dispositivosOnline} / {totals.dispositivosTotal}
                </p>
                <p className="text-xs text-gray-400">online</p>
              </div>
              <Battery className="h-8 w-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Produção Diária */}
        <ChartCard title="Produção Diária">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="data" 
                tick={{ fontSize: 12 }} 
                stroke="#6b7280"
                tickFormatter={(value) => {
                  try {
                    return format(new Date(value), 'dd/MM', { locale: pt });
                  } catch {
                    return value;
                  }
                }}
              />
              <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" unit=" kWh" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="producao" 
                stroke="#f97316" 
                fill="#fed7aa" 
                name="Produção (kWh)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Curva de Previsão */}
        <ChartCard title="Curva de Previsão de Produção (Hoje)">
          <div className="flex items-center gap-2 mb-2">
            {apiConnected && forecast.length > 0 && (
              <Badge className="bg-orange-100 text-orange-800 text-xs">
                Potência instalada: {formatNumber(totals.potenciaInstalada, 1)} kW
              </Badge>
            )}
          </div>
          <ResponsiveContainer width="100%" height={270}>
            <AreaChart data={forecast.length > 0 ? forecast : generateMockForecast()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="hora" 
                tick={{ fontSize: 12 }} 
                stroke="#6b7280"
                tickFormatter={(value) => `${value}h`}
              />
              <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" unit=" kW" />
              <Tooltip 
                formatter={(value) => [`${formatNumber(value, 2)} kW`, 'Previsão']}
                labelFormatter={(value) => `${value}:00`}
              />
              <Area 
                type="monotone" 
                dataKey="power_kw" 
                stroke="#eab308" 
                fill="#fef08a" 
                name="Previsão (kW)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Tabela de Registos 15 em 15 minutos */}
      {apiConnected && readings15min.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-500" />
                Registos de Produção (15 min)
              </h2>
              <Badge variant="outline">{readings15min.length} registos</Badge>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Dispositivos</TableHead>
                    <TableHead className="text-right">Potência Média</TableHead>
                    <TableHead className="text-right">Energia Acumulada</TableHead>
                    <TableHead className="text-right">Leituras</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readings15min.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono text-sm">
                        {format(new Date(record.timestamp), 'dd/MM/yyyy HH:mm', { locale: pt })}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{record.devices_list}</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-orange-600">
                        {formatNumber(record.avg_power, 1)} W
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {formatNumber(record.total_energy, 2)} kWh
                      </TableCell>
                      <TableCell className="text-right text-gray-500">
                        {record.readings.length}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dispositivos */}
      {devices.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-500" />
              Dispositivos Shelly EM
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(summary?.devices || devices).map((device, index) => (
                <Card key={device.device_id} className="border border-gray-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{device.descricao || device.device_id}</p>
                        <p className="text-sm text-gray-500">{device.local}</p>
                      </div>
                      <Badge className={device.online !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {device.online !== false ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-gray-500">Potência</p>
                        <p className="font-semibold text-orange-600">
                          {formatNumber(device.current_power_w || 0, 0)} W
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Energia Total</p>
                        <p className="font-semibold text-green-600">
                          {formatNumber(device.total_energy_kwh || 0, 1)} kWh
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Instalado</p>
                        <p className="font-medium">{device.installed_power_kw || 0} kW</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Última leitura</p>
                        <p className="font-medium text-xs">
                          {device.last_reading 
                            ? format(new Date(device.last_reading), 'HH:mm:ss', { locale: pt })
                            : '-'
                          }
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== Huawei FusionSolar Section ===== */}
      {huaweiStations.length > 0 && (
        <div className="space-y-6" data-testid="huawei-section">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="h-5 w-5 text-orange-500" />
              Huawei FusionSolar
              <Badge variant="outline" className="ml-2 text-xs">
                Dados a cada 5 min
              </Badge>
            </h2>
            <div className="flex items-center gap-3">
              {lastHuaweiRefresh && (
                <span className="text-xs text-gray-500">
                  Última: {format(lastHuaweiRefresh, 'HH:mm:ss', { locale: pt })}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshHuaweiData(true)}
                disabled={refreshingHuawei}
                data-testid="huawei-refresh-now-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshingHuawei ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>

          {huaweiStations.map((station) => {
            const rt = huaweiRealtime[station.id];
            const kpi = rt?.real_kpi || {};
            const social = rt?.social_contribution || {};
            const overview = rt?.station_overview || {};
            const pc = rt?.power_curve || {};

            // Construir dados do gráfico intraday
            const chartIntraday = (pc.x_axis || []).map((label, i) => ({
              hora: label,
              activePower: pc.active_power?.[i] ?? null,
              currentPower: pc.current_power?.[i] ?? null,
            })).filter(p => p.activePower !== null || p.currentPower !== null);

            return (
              <Card key={station.id} className="border-orange-200" data-testid={`huawei-station-${station.id}`}>
                <CardContent className="pt-6 space-y-6">
                  {/* Cabeçalho da estação */}
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <CloudSun className="h-5 w-5 text-orange-500" />
                        {overview.stationName || station.local}
                      </h3>
                      <p className="text-sm text-gray-500">{overview.plantAddress || station.descricao || '-'}</p>
                      {overview.stationDn && (
                        <p className="text-xs text-gray-400 font-mono">{overview.stationDn}</p>
                      )}
                    </div>
                    <Badge className={rt ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {rt ? <><Wifi className="h-3 w-3 mr-1" /> Tempo Real</> : <><WifiOff className="h-3 w-3 mr-1" /> Sem dados</>}
                    </Badge>
                  </div>

                  {/* KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-orange-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Potência Atual</p>
                      <p className="text-xl font-bold text-orange-600">
                        {formatNumber(kpi.realTimePower, 2)} <span className="text-sm font-normal">kW</span>
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Energia Hoje</p>
                      <p className="text-xl font-bold text-yellow-600">
                        {formatNumber(kpi.dailyEnergy, 2)} <span className="text-sm font-normal">kWh</span>
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Mês</p>
                      <p className="text-xl font-bold text-amber-600">
                        {formatNumber(kpi.monthEnergy, 1)} <span className="text-sm font-normal">kWh</span>
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Ano</p>
                      <p className="text-xl font-bold text-blue-600">
                        {formatNumber(kpi.yearEnergy, 1)} <span className="text-sm font-normal">kWh</span>
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Total Acumulado</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatNumber(kpi.cumulativeEnergy, 1)} <span className="text-sm font-normal">kWh</span>
                      </p>
                    </div>
                  </div>

                  {/* Curva intraday */}
                  {chartIntraday.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Curva de Produção do Dia</h4>
                      <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={chartIntraday}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="hora"
                            tick={{ fontSize: 10 }}
                            stroke="#6b7280"
                            interval={Math.floor(chartIntraday.length / 12)}
                          />
                          <YAxis tick={{ fontSize: 11 }} stroke="#6b7280" unit=" kW" />
                          <Tooltip
                            formatter={(value) => [`${formatNumber(value, 2)} kW`, '']}
                            labelFormatter={(value) => `${value}h`}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="activePower"
                            stroke="#f97316"
                            fill="#fed7aa"
                            name="Potência ativa (kW)"
                            connectNulls
                          />
                          <Area
                            type="monotone"
                            dataKey="currentPower"
                            stroke="#eab308"
                            fill="#fef08a"
                            name="Potência atual (kW)"
                            connectNulls
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Contribuição social/ambiental */}
                  {social && Object.keys(social).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Leaf className="h-4 w-4 text-green-600" /> Contribuição Ambiental
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="bg-green-50 border border-green-100 rounded-lg p-3 flex items-center gap-3">
                          <CloudSun className="h-8 w-8 text-green-600" />
                          <div>
                            <p className="text-xs text-gray-500">Redução de CO₂</p>
                            <p className="text-base font-bold text-green-700">
                              {formatNumber(social.co2Reduction, 1)} kg
                            </p>
                            <p className="text-xs text-gray-400">Ano: {formatNumber(social.co2ReductionByYear, 1)} kg</p>
                          </div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-center gap-3">
                          <TreeDeciduous className="h-8 w-8 text-emerald-600" />
                          <div>
                            <p className="text-xs text-gray-500">Árvores equivalentes</p>
                            <p className="text-base font-bold text-emerald-700">
                              {formatNumber(social.equivalentTreePlanting, 0)}
                            </p>
                            <p className="text-xs text-gray-400">Ano: {formatNumber(social.equivalentTreePlantingByYear, 0)}</p>
                          </div>
                        </div>
                        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-center gap-3">
                          <Battery className="h-8 w-8 text-stone-600" />
                          <div>
                            <p className="text-xs text-gray-500">Carvão poupado</p>
                            <p className="text-base font-bold text-stone-700">
                              {formatNumber(social.standardCoalSavings, 1)} kg
                            </p>
                            <p className="text-xs text-gray-400">Ano: {formatNumber(social.standardCoalSavingsByYear, 1)} kg</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tabela filtrável + exportação (Huawei daily history) */}
      {huaweiStations.length > 0 && (
        <Card data-testid="fotovoltaico-table-card">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Sun className="h-5 w-5 text-orange-500" />
                  Registos de Produção Diária
                  <Badge className="ml-1 bg-green-100 text-green-800">Dados Reais</Badge>
                </h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Filter className="h-4 w-4" />
                    <span>{(huaweiDailyHistory.filter((r) =>
                      visibleStationIds.has(r.station_id) &&
                      (tableFilterStation === 'all' || r.station_id === tableFilterStation) &&
                      (tableSearch === '' || r.local?.toLowerCase().includes(tableSearch.toLowerCase()) || r.date.includes(tableSearch))
                    )).length} registos</span>
                  </div>
                  <ExportMenu
                    columns={[
                      { key: 'date', label: 'Data', type: 'date' },
                      { key: 'local', label: 'Local' },
                      { key: 'installed_power_kw', label: 'Pot. Instalada (kW)', type: 'number', decimals: 2 },
                      { key: 'daily_kwh', label: 'Energia Diária (kWh)', type: 'number', decimals: 2 },
                      { key: 'real_time_power_kw', label: 'Pot. Tempo Real (kW)', type: 'number', decimals: 2 },
                      { key: 'cumulative_kwh', label: 'Cumulativa (kWh)', type: 'number', decimals: 2 },
                    ]}
                    rows={huaweiDailyHistory.filter((r) =>
                      visibleStationIds.has(r.station_id) &&
                      (tableFilterStation === 'all' || r.station_id === tableFilterStation) &&
                      (tableSearch === '' || r.local?.toLowerCase().includes(tableSearch.toLowerCase()) || r.date.includes(tableSearch))
                    )}
                    filename="producao_fotovoltaica"
                    title="Relatório de Produção Fotovoltaica"
                    subtitle={`Período: últimos ${periodFilter.days || 30} dias · Estações visíveis: ${visibleStationIds.size}`}
                    testId="fotovoltaico-export"
                  />
                </div>
              </div>

              {/* Cards de seleção (toggle) por estação */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {huaweiStations.map((s) => {
                  const isVisible = visibleStationIds.has(s.id);
                  const totalKwh = huaweiDailyHistory
                    .filter((r) => r.station_id === s.id)
                    .reduce((sum, r) => sum + (r.daily_kwh || 0), 0);
                  return (
                    <div
                      key={s.id}
                      onClick={() => setVisibleStationIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                        return next;
                      })}
                      onDoubleClick={() => setVisibleStationIds(new Set([s.id]))}
                      className={`cursor-pointer rounded-lg border-l-4 border p-3 transition-all ${
                        isVisible ? 'border-l-orange-500 shadow-sm' : 'border-l-gray-300 opacity-50 grayscale hover:opacity-75'
                      }`}
                      data-testid={`fotovoltaico-station-toggle-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {isVisible ? <Eye className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" /> : <EyeOff className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                          <p className="text-sm font-medium text-gray-900 truncate">{s.local}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{s.installed_power_kw} kW</Badge>
                      </div>
                      <div className="mt-2 flex items-end justify-between">
                        <span className="text-xs text-gray-500">{periodFilter.days || 30}d</span>
                        <span className="text-base font-bold text-gray-900 tabular-nums">{formatNumber(totalKwh, 1)} kWh</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Pesquisar local ou data..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    className="pl-10"
                    data-testid="fotovoltaico-search-input"
                  />
                </div>
                <Select value={tableFilterStation} onValueChange={setTableFilterStation}>
                  <SelectTrigger data-testid="fotovoltaico-filter-station">
                    <SelectValue placeholder="Todas as estações" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as estações</SelectItem>
                    {huaweiStations.filter((s) => visibleStationIds.has(s.id)).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.local}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Data</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead className="text-right">Pot. Instalada</TableHead>
                      <TableHead className="text-right">Energia Diária</TableHead>
                      <TableHead className="text-right">Pico (kW)</TableHead>
                      <TableHead className="text-right">Cumulativa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const filtered = huaweiDailyHistory.filter((r) =>
                        visibleStationIds.has(r.station_id) &&
                        (tableFilterStation === 'all' || r.station_id === tableFilterStation) &&
                        (tableSearch === '' || r.local?.toLowerCase().includes(tableSearch.toLowerCase()) || r.date.includes(tableSearch))
                      );
                      if (filtered.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                              Sem registos no período. As leituras são recolhidas automaticamente a cada 5 min.
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filtered.slice(0, 500).map((row, idx) => (
                        <TableRow key={`${row.station_id}-${row.date}-${idx}`} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{format(parseISO(row.date), 'dd/MM/yyyy', { locale: pt })}</TableCell>
                          <TableCell>{row.local}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-gray-500">{formatNumber(row.installed_power_kw, 2)} kW</TableCell>
                          <TableCell className="text-right font-mono tabular-nums font-semibold text-orange-700">{formatNumber(row.daily_kwh, 2)} kWh</TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{formatNumber(row.real_time_power_kw, 2)} kW</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-gray-500">{formatNumber(row.cumulative_kwh, 2)} kWh</TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mensagem para configurar */}
      {!apiConnected && huaweiStations.length === 0 && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6 text-center">
            <Sun className="h-12 w-12 mx-auto mb-4 text-orange-400" />
            <h3 className="text-lg font-semibold text-gray-900">Configure os seus dispositivos Shelly</h3>
            <p className="text-gray-600 mt-2 mb-4">
              Adicione dispositivos Shelly EM para monitorizar a produção fotovoltaica em tempo real
            </p>
            <Button onClick={() => navigate('/configuracoes/fotovoltaico')}>
              <Settings className="h-4 w-4 mr-2" />
              Configurar Dispositivos
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Gerar previsão mock para demonstração
const generateMockForecast = () => {
  const forecast = [];
  const hourlyFactors = {
    0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
    6: 0.05, 7: 0.15, 8: 0.35, 9: 0.55, 10: 0.75,
    11: 0.88, 12: 0.95, 13: 1.0, 14: 0.95, 15: 0.85,
    16: 0.70, 17: 0.50, 18: 0.30, 19: 0.10, 20: 0.02,
    21: 0, 22: 0, 23: 0
  };
  
  for (let hour = 0; hour < 24; hour++) {
    forecast.push({
      hora: hour,
      power_kw: 10 * (hourlyFactors[hour] || 0) * 0.8
    });
  }
  
  return forecast;
};

export default Fotovoltaico;
